import { AuthConfig, User } from "@/types";
import { Amplify } from "aws-amplify";
import {
  getCurrentUser,
  signIn,
  signOut,
  fetchAuthSession,
} from "aws-amplify/auth";
import { Hub, HubCallback } from "@aws-amplify/core";

export class AuthService {
  private config: AuthConfig;
  private authStateListeners: Array<
    (isAuthenticated: boolean, user: User | null) => void
  > = [];
  private cachedCredentials: {
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
    expiration: Date;
  } | null = null;
  private hubListener?: () => void;

  constructor(config: AuthConfig) {
    this.config = config;

    // Configure Amplify
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: config.userPoolId,
          userPoolClientId: config.userPoolWebClientId,
          identityPoolId: config.identityPoolId,
        },
      },
    });

    // Set up Hub listener for auth events
    this.setupHubListener();
  }

  private setupHubListener(): void {
    const hubCallback: HubCallback = async ({ payload }) => {
      if (payload.event === "signedIn") {
        this.clearCachedCredentials();
        const user = await this.getCurrentUser();
        this.notifyAuthStateChange(true, user);
      } else if (payload.event === "signedOut") {
        this.clearCachedCredentials();
        this.notifyAuthStateChange(false, null);
      } else if (payload.event === "tokenRefresh") {
        this.clearCachedCredentials();
      }
    };

    this.hubListener = Hub.listen("auth", hubCallback);
  }

  private clearCachedCredentials(): void {
    this.cachedCredentials = null;
  }

  private notifyAuthStateChange(
    isAuthenticated: boolean,
    user: User | null,
  ): void {
    this.authStateListeners.forEach((listener) =>
      listener(isAuthenticated, user),
    );
  }

  addAuthStateListener(
    listener: (isAuthenticated: boolean, user: User | null) => void,
  ): void {
    this.authStateListeners.push(listener);
  }

  removeAuthStateListener(
    listener: (isAuthenticated: boolean, user: User | null) => void,
  ): void {
    const index = this.authStateListeners.indexOf(listener);
    if (index > -1) {
      this.authStateListeners.splice(index, 1);
    }
  }

  async login(email: string, password: string): Promise<void> {
    try {
      await signIn({
        username: email,
        password: password,
      });
      this.clearCachedCredentials();
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await signOut();
      this.clearCachedCredentials();
    } catch (error) {
      console.error("Logout failed:", error);
      throw error;
    }
  }

  async getSession(): Promise<any> {
    try {
      const session = await fetchAuthSession();
      return session;
    } catch (error) {
      console.error("Failed to get session:", error);
      return null;
    }
  }

  async getIdToken(): Promise<string | null> {
    try {
      const session = await this.getSession();
      return session?.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error("Failed to get ID token:", error);
      return null;
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const currentUser = await getCurrentUser();
      if (!currentUser) return null;

      return {
        email: currentUser.signInDetails?.loginId || "",
        sub: currentUser.userId,
      };
    } catch (error) {
      console.error("Failed to get current user:", error);
      return null;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    try {
      const session = await this.getSession();
      return !!session?.tokens?.accessToken;
    } catch (error) {
      return false;
    }
  }

  getRegion(): string {
    return this.config.region;
  }

  async getAWSCredentials(): Promise<{
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken?: string;
  }> {
    // Return cached credentials if still valid
    if (
      this.cachedCredentials &&
      this.cachedCredentials.expiration > new Date()
    ) {
      return {
        accessKeyId: this.cachedCredentials.accessKeyId,
        secretAccessKey: this.cachedCredentials.secretAccessKey,
        sessionToken: this.cachedCredentials.sessionToken,
      };
    }

    try {
      const session = await this.getSession();
      if (!session) {
        throw new Error("No valid session available");
      }

      // Get credentials from Amplify session
      const credentials = session.credentials;
      if (!credentials) {
        throw new Error("No credentials available in session");
      }

      // Cache the credentials with expiration (default to 1 hour if not provided)
      const expiration = new Date(
        Date.now() +
          (credentials.expiration?.getTime() || Date.now() + 3600000),
      );

      this.cachedCredentials = {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
        expiration,
      };

      return {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
      };
    } catch (error) {
      console.error("Error getting AWS credentials:", error);
      throw error;
    }
  }

  // Clean up method to remove Hub listener
  dispose(): void {
    if (this.hubListener) {
      this.hubListener();
    }
  }
}
